import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function confirmThen(message, fn) {
  return () => { if (window.confirm(message)) fn(); };
}

export default function ActionBar({
  status,
  onRecalculate,
  onPdf,
  onSend,
  onAccept,
  onCancel,
  onCopy,
  onSaveClose,
  onClose,
}) {
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const wrap = (fn) => async () => {
    try { setBusy(true); await fn(); } finally { setBusy(false); }
  };

  const isDraft = status === "DRAFT";
  const isSent = status === "SENT";
  const isCancelled = status === "CANCELLED";

  return (
    <Paper sx={{ p: 1.5, position: "sticky", bottom: 0, zIndex: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Destructive action kept apart on the left so it isn't mistaken for "cancel my edit". */}
        {!isCancelled && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setConfirmCancel(true)}
            disabled={busy}
          >
            Cancel quote…
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {isDraft && (
          <Button startIcon={<RefreshIcon />} onClick={wrap(onRecalculate)} disabled={busy}>
            Recalculate
          </Button>
        )}
        {!isDraft && (
          <Button startIcon={<ContentCopyIcon />} onClick={wrap(onCopy)} disabled={busy}>
            Copy to new draft
          </Button>
        )}
        <Button startIcon={<PictureAsPdfIcon />} onClick={wrap(onPdf)} disabled={busy}>
          {isDraft ? "Preview PDF" : "Generate PDF"}
        </Button>
        {isDraft && (
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={confirmThen("Mark this quote as sent? It will be locked from further edits.", wrap(onSend))}
            disabled={busy}
          >
            Mark as sent
          </Button>
        )}
        {isSent && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={confirmThen("Mark this quote as ACCEPTED?", wrap(onAccept))}
            disabled={busy}
          >
            Accept
          </Button>
        )}
        {isDraft ? (
          <Button variant="contained" startIcon={<SaveIcon />} onClick={wrap(onSaveClose)} disabled={busy}>
            Save and close
          </Button>
        ) : (
          <Button onClick={wrap(onClose)} disabled={busy}>Close</Button>
        )}
      </Stack>

      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel this quote?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The whole quote is marked as cancelled and locked: it can't be edited, sent or
            accepted any more. Your changes so far are already saved; this is not an undo.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1.5 }}>
            You can still copy it to a new draft later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCancel(false)} autoFocus>Keep the quote</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => { setConfirmCancel(false); wrap(onCancel)(); }}
          >
            Cancel quote
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
